import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import "./reports.css";
import ReportFilter from "./ReportFilter";
import { fetchReports } from "../../utils/Api";
import * as XLSX from "xlsx";
import moment from "moment";
import CustomBreadcrumbs from "../../components/CustomBreadcrumbs";
import ReportBtnGroup from "./ReportBtnGroup";
import { useSelector } from "react-redux";
import CustomBarChart from "../../components/CustomBarChart";
import { getLocationName, getMealName } from "../../utils/mealUtils";

const ReportPage = () => {
  const [show, setShow] = useState(false);
  const [chartData, setChartData] = useState({
    series: [],
    categories: [],
    exportDates: [],
  });
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const { mealType, location, startDate, endDate, dates } = useSelector(
    (state) => state.report
  );

  const handleButtonClick = () => setShow(true);

  const fetchReportsData = async () => {
    const effectiveStartDate = tempStartDate
      ? moment(tempStartDate.$d).format("YYYY-MM-DD")
      : startDate
      ? moment(startDate.$d).format("YYYY-MM-DD")
      : dates?.startDate;
    const effectiveEndDate = tempEndDate
      ? moment(tempEndDate.$d).format("YYYY-MM-DD")
      : endDate
      ? moment(endDate.$d).format("YYYY-MM-DD")
      : dates?.endDate;

    try {
      setIsLoading(true);
      if (!mealType || !location || !effectiveStartDate || !effectiveEndDate)
        return;
      const data = await fetchReports(
        mealType,
        location,
        effectiveStartDate,
        effectiveEndDate
      );

      if (!data || !Array.isArray(data.data)) {
        throw new Error("Invalid data structure");
      }

      // Sort the data by mealDate in descending order
      // const data.data = data.data.sort((a, b) =>
      //   moment(b.mealDate).isBefore(moment(a.mealDate)) ? -1 : 1
      // );

      const categories = data.data.map((item) =>
        moment(item.mealDate).format("MMM D")
      );
      const exportDates = data.data.map((item) =>
        moment(item.mealDate).format("MMM D, YY")
      );
      const guestsData = data.data.map((item) => {
        const guest = item.details.find(
          (detail) => detail.mealType === "guest"
        );
        return guest ? guest.mealCount : 0;
      });
      const selfData = data.data.map((item) => {
        const self = item.details.find((detail) => detail.mealType === "self");
        return self ? self.mealCount : 0;
      });

      const contractData = data.data.map((item) => {
        const self = item.details.find(
          (detail) => detail.mealType === "ctr-self"
        );
        return self ? self.mealCount : 0;
      });

      setChartData({
        series: [
          { name: "Self", data: selfData },
          { name: "Guests", data: guestsData },
          { name: "Contract", data: contractData },
        ],
        categories,
        exportDates,
      });
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching report data:", error);
      setChartData({
        series: [],
        categories: [],
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReportsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, mealType, location, dates]);

  const handleExport = () => {
    const headers = [
      "Date",
      "Self",
      "Contract",
      "Guests",
      "Meal Type",
      "Total Meal",
      "Location",
    ];
    const rows = chartData.exportDates.map((date, index) => {
      const guests = chartData.series[1]?.data[index] || 0;
      const self = chartData.series[0]?.data[index] || 0;
      const contract = chartData.series[2]?.data[index] || 0;
      return {
        Date: date,
        Self: self,
        Guests: guests,
        Contract: contract,
        "Meal Type": getMealName(mealType),
        "Total Meal": guests + self + contract,
        Location: getLocationName(location),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const rawStart = tempStartDate?.$d || startDate?.$d || dates?.startDate;
    const rawEnd = tempEndDate?.$d || endDate?.$d || dates?.endDate;

    const startMoment = moment(rawStart);
    const endMoment = moment(rawEnd);

    let dateSegment = "";

    if (startMoment.isSame(endMoment, "day")) {
      dateSegment = startMoment.format("DDMMMYY");
    } else if (endMoment.diff(startMoment, "days") > 25) {
      dateSegment = endMoment.format("MMM,YY");
    } else {
      dateSegment = `${startMoment.format("DDMMMYY")}_to_${endMoment.format(
        "DDMMMYY"
      )}`;
    }

    XLSX.writeFile(
      workbook,
      `${getMealName(mealType)}-${getLocationName(
        location
      )}-Reports-${dateSegment}.xlsx`
    );
  };

  const breadcrumbItems = [{ label: "Home", path: "/cms/dashboard" }];

  const handleBarClick = (selectedIndex) => {
    const selectedDate = chartData.exportDates[selectedIndex];
    navigate(
      `/cms/reports-detail/${moment(selectedDate).format("YYYY-MM-DD")}`,
      { state: { mealType, location } }
    );
  };

  return (
    <>
      <CustomBreadcrumbs items={breadcrumbItems} currentLabel="Reports" />
      <Box m={4} bgcolor="#fff">
        <ReportFilter
          name={`Report - ${getMealName(mealType)}`}
          handleButtonClick={handleButtonClick}
          onClick={handleExport}
          tempStartDate={tempStartDate}
          tempEndDate={tempEndDate}
          setTempStartDate={setTempStartDate}
          setTempEndDate={setTempEndDate}
          startDate={startDate}
          endDate={endDate}
        />
        {isLoading ? ( // Render loading spinner when data is being fetched
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={375}
          >
            <CircularProgress />
          </Box>
        ) : (
          <CustomBarChart
            chartData={{
              series: [
                {
                  name: "Self",
                  label: "Self",
                  data: chartData.series[0]?.data || [],
                  color: "#A3E7C1",
                  stack: "total",
                },
                {
                  name: "Guests",
                  label: "Guest",
                  data: chartData.series[1]?.data || [],
                  color: "#2A85FF",
                  stack: "total",
                },
                {
                  name: "Contract",
                  label: "Contract",
                  data: chartData.series[2]?.data || [],
                  color: "#EEA53E",
                  stack: "total",
                },
              ],
              categories: chartData.categories,
            }}
            barWidth={40}
            onBarClick={handleBarClick}
          />
        )}
        <Box mt={2} textAlign={"center"}>
          <Typography variant="caption">{getMealName()} Consumed</Typography>
          {/* <ReportBtnGroup /> */}
        </Box>
      </Box>
    </>
  );
};

export default ReportPage;
